import React, {useState} from 'react';
import { Link } from 'react-router-dom';
import SideBar  from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import { CopyBlock, dracula } from "react-code-blocks";
import { HashLink } from 'react-router-hash-link';


const JavaScriptCallbacks = () => {
  const [language, changeLanguage] = useState("javascript");
  const [lineNumbers, toggleLineNumbers] = useState(false);
  const code = (
  'text',
  `
  function greet(name, callback) {
      console.log(\`Hello \${name}!\`);
      callback(); // invoking the callback function
  }

  function sayGoodbye() {
      console.log("Goodbye!");
  }

  console.log(greet("Alice", sayGoodbye)); // passing sayGoodbye as a callback
  `
  );
  const code2 = (
  'text',
  `
  console.log('First');
  console.log('Second');
  console.log('Third');
  `
  );
  const code3 = (
  'text',
  `
  First
  Second
  Third
  `
  );
  const code4 = (
  'text',
  `console.log('First');
  // Set timeout for 2 seconds
  setTimeout(() => console.log('Second'), 2000);
  console.log('Third');
  `
  );
  const code5 = (
  'text',
  `
  First
  Third
  Second
  `
  );
  const code6 = (
  'text',
  `const fs = require("fs");

  try {
  // Storing the data form file.txt file in data
  const data = fs.readFileSync("./file.txt", "utf8");
  console.log(data); // To show the data
  } catch (error) {
  console.error(error); // if error is catched
  }
  `
  );
  const code7 = (
  'text',
  `Hello, text from file.txt
  `
  );
  const code8 = (
  'text',
  `document.getElementById("myButton").addEventListener("click", function() {
    console.log("Button clicked!");
  });`
  );
  const code9 = (
  'text',
  `const numbers = [1, 2, 3, 4, 5];
numbers.forEach(function(num) {
console.log(num * 2);
  });`
  );
  const code10 = (
  'text',
  `const myPromise = new Promise((resolve, reject) => {
    // Asynchronous operation
    // If successful, call resolve() with the result
    // If there's an error, call reject() with the error
  });`
  );
  const code11 = (
  'text',
  `const myPromise = new Promise((resolve, reject) => {
    setTimeout(() => {
        const data = 'Some fetched data';
        resolve(data); // Resolving the promise with data
        // Or reject(new Error('Error message')) if there's an error
    }, 2000); // Simulating an asynchronous operation
});

myPromise.then((data) => {
    console.log('Data received:', data);
}).catch((error) => {
    console.error('Error occurred:', error);
});
`
  );
  const code12 = (
  'text',
  `const myPromise = new Promise((resolve, reject) => {
    resolve(1);
});

  myPromise.then((result) => {
      console.log(result); // Output: 1
      return result * 2; // Return a new promise
  }).then((result) => {
      console.log(result); // Output: 2
      return result * 2; // Return a new promise
  }).then((result) => {
      console.log(result); // Output: 4
  }).catch((error) => {
      console.error('Error occurred:', error);
  });`
  );
  const code13 = (
  'text',
  `const promise1 = Promise.resolve('First promise');
  const promise2 = new Promise((resolve, reject) => {
    setTimeout(resolve, 1000, 'Second promise');
  });
  const promise3 = new Promise((resolve, reject) => {
      setTimeout(resolve, 2000, 'Third promise');
  });

  Promise.all([promise1, promise2, promise3])
  .then((values) => {
      console.log(values); // Output: ['First promise', 'Second promise', 'Third promise']
  });`
  );
  const code14 = (
  'text',
  `const promise1 = new Promise((resolve, reject) => {
    setTimeout(resolve, 1000, 'First promise');
  });
  const promise2 = new Promise((resolve, reject) => {
      setTimeout(resolve, 2000, 'Second promise');
  });

  Promise.race([promise1, promise2])
    .then((value) => {
      console.log(value); // Output: 'First promise'
    });`
  );
  return(
    <HTMLCourseNavProvider>
    <div className="container">
    <div className="row">
    <div>
    <SideBar  />
    </div>
    <div className="col-md-12">

    <div className="alert alert-secondary" role="alert">
    <h3><strong>Callbacks</strong></h3>
    <p><strong>callbacks</strong> are functions that are passed as arguments or parameters to other functions and are invoked when certain actions or events occur. Callbacks are commonly used in asynchronous programming, event handling, and functional programming paradigms. Here's an explanation with examples we shall talk about <strong>asynchronous programming</strong> later:</p>

    <p><HashLink className="text-primary" smooth to='/javascript-functions/#function-parameter'><strong>See Function Parameters</strong></HashLink></p>

    <CopyBlock
    language={language}
    text={code}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
    <p class="mt-4 mb-4">Let's explain what's happening in the code above. We have a function called <strong>greet()</strong> which takes two parameters and one of those parameters is a callback function because it is passed as an argument. We're also naming the function <strong>callback</strong>, which could as well be any name we decide to use.</p>
    <p>So our function <strong>greet()</strong> has two parameters &mdash; one is a normal parameter called <strong>name</strong> another is callback function called <strong>callback</strong>.</p>
    <p>Next, we need to make the function callable by invoking an instance of it <strong>callback()</strong>.</p>
    <p>Next, we create a function that will be used as the callback, which is <strong>sayGoodbye()</strong> and then we specify the given parameters, just like we do for <HashLink className="text-primary" smooth to='/javascript-functions/#function-parameter'><strong>Function Parameters</strong></HashLink></p>
    <h4><strong>Callbacks in Event Handling</strong></h4>
    <CopyBlock
    language={language}
    text={code8}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
    <br />
    <p>In this example, an anonymous function is passed as a callback to the <strong>addEventListener</strong> method. It listens for the "click" event on the HTML element with the id "myButton" and logs "Button clicked!" to the console when the event occurs.</p>
    <h4><strong>Callbacks in Array Methods</strong></h4>
    <CopyBlock
    language={language}
    text={code9}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
    <br />
    <p>Here, an anonymous function is passed as a callback to the <strong>forEach</strong> method of the numbers array. It multiplies each element by 2 and logs the result.</p>
    </div>
    <div className="alert alert-secondary" role="alert">
     <h3><strong>What is Synchronous JavaScript?</strong></h3>
    <p>At its core JavaScript language is synchronous. Synchronous means the code runs in a particular sequence of instructions given in the program. Each instruction waits for the previous instruction to complete its execution.</p>
    <p className="mb-4">See the following code example of synchronous JavaScript</p>
    <CopyBlock
    language={language}
    text={code2}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
    <h4 className="mb-4 mt-4"><strong>Output</strong></h4>
    <CopyBlock
    language={language}
    text={code3}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
    <p className="mt-3">In the above JavaScript code snippet, three lines of instructions are given. Every instruction runs once after the previous instruction gets executed. Due to this synchronous nature of javascript, we get the output of console logs in the sequence we provided in the program.</p>
    <p>When we say synchronous vs asynchronous JavaScript the execution sequence of instructions in a program is what differentiates them.</p>
    <h4><strong>Reading Files Synchronously</strong></h4>
    <p>Consider the following coding example, here synchronous javascript is used to read data from the <span className="text-danger">file.txt file</span>. Here we are using the <span className="text-danger">readFileSync()</span> function which works synchronously without any promise.</p>
    <CopyBlock
    language={language}
    text={code6}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
    <br />
    <h4 className="mb-4 mt-4"><strong>Output</strong></h4>
    <CopyBlock
    language={language}
    text={code7}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
    <br />
    <p>NB: You will need to have Node.js installed to run the above example. Please check out our Node JS course.</p>
    </div>
    <div className="alert alert-secondary" role="alert">
    <h3><strong>What is Asynchronous JavaScript?</strong></h3>
    <p>As we saw in the synchronous code example, all instructions in the program execute one after another, and every instruction waits for its previous instruction to get executed. Due to this nature of synchronous programming, sometimes important instructions get blocked due to some previous instructions, which causes a delay in the user interface. Asynchronous code execution allows to execution next instructions immediately and doesn't block the flow because of previous instructions.</p>
    <h3><strong>The Restaurant Paradox</strong></h3>
    <p>Suppose you went to a restaurant to order for a meal. You sit down and you place an order. The waiter takes your order to the chef. Does the waiter wait for your meal to be prepared and served before attending to the next person?</p>
    <p>Well, if they do, then you would have a queue of angry and hungry people. But, thankfully, the waiter doesn't do that. While your order is being prepared, another customr is attended to and whenever your order is ready, you are then served. This is an <strong>Asynchronous</strong> process. </p>
    <p className="mb-4">See the following coding example to understand how javascript works asynchronously: </p>
    <CopyBlock
    language={language}
    text={code4}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
    <h4 className="mb-4 mt-4"><strong>Output</strong></h4>
    <CopyBlock
    language={language}
    text={code5}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
    <p className="mt-4">As we can see in the output of the above code example, Third gets printed before Second, because of the asynchronous execution of the code. Here setTimeout() function waits for 2 seconds, and in the meantime, the next instruction gets executed without waiting for the previous one to complete the execution.</p>
    </div>

    <div className="alert alert-secondary" role="alert">
    <h3><strong>JavaScript Promises</strong></h3>
    <p>Promises in JavaScript are a way to handle asynchronous operations, such as fetching data from a server, reading a file, or executing a timeout, in a more organized and manageable manner. Promises represent a value that may be available now, in the future, or never.</p>
    <h4 className="mb-4 mt-4"><strong>Syntax</strong></h4>
    <CopyBlock
    language={language}
    text={code10}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
    <h4 className="mb-4 mt-4"><strong>Example</strong></h4>
    <CopyBlock
    language={language}
    text={code11}
    showLineNumbers={lineNumbers}
    theme={dracula}
    wrapLines={true}
    codeBlock
    />
   <h4 className="mb-4 mt-4"><strong>Chaining Promises</strong></h4>
   <CopyBlock
   language={language}
   text={code12}
   showLineNumbers={lineNumbers}
   theme={dracula}
   wrapLines={true}
   codeBlock
   />
   <h4 className="mb-4 mt-4"><strong>Promise.all()</strong></h4>
   <CopyBlock
   language={language}
   text={code13}
   showLineNumbers={lineNumbers}
   theme={dracula}
   wrapLines={true}
   codeBlock
   />
   <h4 className="mb-4 mt-4"><strong>Promise.race()</strong></h4>
   <CopyBlock
   language={language}
   text={code14}
   showLineNumbers={lineNumbers}
   theme={dracula}
   wrapLines={true}
   codeBlock
   />
   <br />
   <p>Promises provide a more structured way to handle asynchronous operations in JavaScript, making code easier to read, write, and maintain compared to traditional callback-based approaches. They are essential in modern JavaScript programming for dealing with asynchronous tasks efficiently and elegantly.</p>
  </div>

    </div>
    </div>
    </div>
    </HTMLCourseNavProvider>
  )
}

export default JavaScriptCallbacks
